import type {
  LibraryFieldDef,
  LibraryFormSchema,
} from '@/features/metadata-library/types/metadataLibrary';
import { t } from '@lingui/core/macro';
import { z } from 'zod';
import { LibraryFormRulesError, catalogEnum, catalogOptions, catalogSchema } from './catalog';
import {
  type LibraryScalarValidationOptions,
  libraryFieldValueError,
  libraryObjectValueError,
} from './validate';

function requireSchema(
  fields: readonly LibraryFieldDef[],
  fieldKey: string,
  type: LibraryFormSchema['type'],
) {
  const schema = catalogSchema(fields, fieldKey);
  if (schema.type !== type)
    throw new LibraryFormRulesError(t`The validation rules for ${fieldKey} could not be loaded.`);
  try {
    if (schema.pattern) new RegExp(schema.pattern, 'u');
  } catch {
    throw new LibraryFormRulesError(t`The validation rules for ${fieldKey} could not be loaded.`);
  }
  if (
    schema.multipleOf !== undefined &&
    (!Number.isFinite(schema.multipleOf) || schema.multipleOf <= 0)
  ) {
    throw new LibraryFormRulesError(t`The validation rules for ${fieldKey} could not be loaded.`);
  }
  return schema;
}

export function textInputSchema(
  fields: readonly LibraryFieldDef[],
  fieldKey: string,
  options?: LibraryScalarValidationOptions,
) {
  const schema = requireSchema(fields, fieldKey, 'string');
  return z.string().superRefine((value, context) => {
    const message = libraryFieldValueError(schema, value, options);
    if (message) context.addIssue({ code: 'custom', message });
  });
}

export function enumInputSchema(
  fields: readonly LibraryFieldDef[],
  fieldKey: string,
  options?: LibraryScalarValidationOptions,
) {
  catalogEnum(fields, fieldKey);
  catalogOptions(fields, fieldKey);
  return textInputSchema(fields, fieldKey, options);
}

export function integerInputSchema(
  fields: readonly LibraryFieldDef[],
  fieldKey: string,
  options?: LibraryScalarValidationOptions,
) {
  const schema = requireSchema(fields, fieldKey, 'integer');
  return z.string().superRefine((value, context) => {
    const message = libraryFieldValueError(schema, value, options);
    if (message) context.addIssue({ code: 'custom', message });
  });
}

export function coordinateInputSchema(
  fields: readonly LibraryFieldDef[],
  fieldKey: string,
  options?: LibraryScalarValidationOptions,
) {
  const schema = requireSchema(fields, fieldKey, 'number');
  return z.string().superRefine((value, context) => {
    const message = libraryFieldValueError(schema, value, options);
    if (message) context.addIssue({ code: 'custom', message });
  });
}

export function handSizeInputSchema(fields: readonly LibraryFieldDef[]) {
  const schema = requireSchema(fields, 'handSizeCm', 'object');
  const properties = Object.entries(schema.properties ?? {}).map(([fieldKey, formSchema]) => ({
    fieldKey,
    formSchema,
  }));
  requireSchema(properties, 'wristCenterToMiddleFingertipCm', 'number');
  requireSchema(properties, 'thumbToPinkySpreadCm', 'number');
  requireSchema(properties, 'middleFingertipToKnuckleCm', 'number');
  return z
    .object({
      wristCenterToMiddleFingertipCm: z.string(),
      thumbToPinkySpreadCm: z.string(),
      middleFingertipToKnuckleCm: z.string(),
    })
    .strict()
    .superRefine((value, context) => {
      const message = libraryObjectValueError(schema, value);
      if (message) context.addIssue({ code: 'custom', message });
    });
}
